import { Suspense, createElement, useState } from "react"
import { AppLayout } from "app/core/layouts/AppLayout"
import { useRouter } from "next/router"
import { BlitzPage } from "@blitzjs/next"
import { invalidateQuery, useMutation, useQuery } from "@blitzjs/rpc"
import { gSSP } from "blitz-server"
import Loading from "app/core/components/Loading"
import clsx from "clsx"
import {
  getMaxNumberOfDiscountedStudents,
  getUpgradeSubscriptionDiscount,
  USDNoCents,
} from "app/orders/utils"
import { getMapTestingMonthlyPrices, getMapTestingYearlyPrices } from "integrations/stripe/server"
import assert from "utils/assert"
import Form, { FORM_ERROR } from "app/core/components/Form"
import LabeledTextField from "app/core/components/LabeledTextField"
import LabeledCheckboxField from "app/core/components/LabeledCheckboxField"
import { Field } from "react-final-form"
import plur from "plur"
import startMapTestingSubscription from "app/billing/mutations/startMapTestingSubscription"
import { computeMapTestingSubscriptionTotal, stripePromise } from "integrations/stripe/client"
import LabeledCreditCardField from "app/core/components/LabeledCreditCardField"
import { Elements } from "@stripe/react-stripe-js"
import { MapTestingSubscriptionCheckoutInput } from "app/billing/validations"
import * as z from "zod"
import { useStripe, useElements, CardElement as StripeCardElement } from "@stripe/react-stripe-js"
import { Button } from "app/core/components/Button"
import { ButtonLink } from "app/core/components/ButtonLink"
import getSubscriptionUpgradeStatus from "app/orders/queries/getSubscriptionUpgradeStatus"
import getPrices from "app/orders/queries/getPrices"
import { useCurrentUser } from "app/core/hooks/useCurrentUser"
import getSubscriptionDetails from "app/orders/queries/getSubscriptionDetails"
import getTestCredits from "app/billing/queries/getTestCredits"
import UpdatePaymentForm from "app/billing/components/UpdateSubscriptionPaymentForm"
import cancelSubscription from "app/map-testing/mutations/cancelSubscription"
import toast from "react-hot-toast"

type PageProps = {
  discountPrices: {
    base: number
    discounted: number
  }
  prices: {
    month: {
      first: number
      rest: number
    }
    year: {
      first: number
      rest: number
    }
  }
  userId: number | null
}

export const getServerSideProps = gSSP<PageProps>(async ({ ctx }) => {
  const prices = {
    month: await getMapTestingMonthlyPrices(),
    year: await getMapTestingYearlyPrices(),
  }

  const pricesForDiscount = await getPrices({})

  return {
    props: {
      discountPrices: {
        base: pricesForDiscount.BASE_PER_STUDENT,
        discounted: pricesForDiscount.EXTRA_PER_STUDENT_DISCOUNT,
      },
      prices,
      userId: ctx.session.userId,
    },
  }
})

function ManageSubscriptions({ onCancelSubscription }) {
  const currentUser = useCurrentUser()
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false)
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false)
  const isSubscribed = Boolean(currentUser?.organization.mapTestingSubscriptionId)
  const [subscriptionDetails] = useQuery(
    getSubscriptionDetails,
    {
      mapTestingSubscriptionId: currentUser?.organization?.mapTestingSubscriptionId!,
    },
    { enabled: isSubscribed && currentUser?.organization !== null }
  )
  const [cancelSubscriptionMutation, { isPending: isLoading }] = useMutation(cancelSubscription)

  const renewalDate = subscriptionDetails ? new Date(subscriptionDetails.renewalDate) : null
  const willRenew = !subscriptionDetails?.cancelAtEnd

  const [testCredits] = useQuery(getTestCredits, {})
  if (testCredits === undefined) return null

  const handleCancelClick = () => {
    setIsCancelModalOpen(true)
  }
  const handleConfirmCancel = async () => {
    setIsCancelModalOpen(false)

    const subscriptionId = currentUser?.organization.mapTestingSubscriptionId
    try {
      const { data } = await cancelSubscriptionMutation({
        subscriptionId: subscriptionId!,
        cancelImmediately: testCredits < 1,
      })

      if (data) {
        toast.success(() => <div>Subscription has been canceled.</div>)
        invalidateQuery(getSubscriptionDetails)
        onCancelSubscription()
      } else {
        console.error("Failed to cancel subscription:", data)
        toast.error(() => (
          <div>Error with canceling subscription, please contact ellen@homeschoolboss.com</div>
        ))
      }
    } catch (error) {
      console.error("Error canceling subscription:", error)
      toast.error(() => (
        <div>Error with canceling subscription, please contact ellen@homeschoolboss.com</div>
      ))
    }
  }

  const handleCloseModal = () => {
    setIsCancelModalOpen(false)
  }

  const handleUpdatePaymentClick = () => {
    setIsUpdateModalOpen(true)
  }

  const handleCloseUpdateModal = () => {
    setIsUpdateModalOpen(false)
  }

  return (
    <>
      <div className="col-span-3 px-4 py-6 bg-white lg:p-12">
        <h3 className="text-2xl font-extrabold leading-8 text-gray-900 sm:text-3xl sm:leading-9">
          Manage Subscription
        </h3>

        <div className="mt-8 lg:grid lg:grid-cols-1 lg:gap-y-5">
          <div className="flex items-start lg:col-span-1">
            <p className="text-sm leading-5 text-gray-700">
              You have {testCredits} test credits available
            </p>
          </div>
          <div className="flex items-center justify-between mt-5 lg:col-span-1 lg:mt-0">
            {renewalDate && willRenew ? (
              <p className="text-sm leading-5 text-gray-700">
                Subscription will be renewed on {renewalDate.toLocaleDateString()} with card ending{" "}
                {subscriptionDetails?.paymentInfo?.last4}
              </p>
            ) : (
              <p className="text-sm leading-5 text-gray-700">
                Subscription credits will expire on {renewalDate!.toLocaleDateString()} and will not
                be renewed.
              </p>
            )}
          </div>

          {willRenew && (
            <div className="flex items-center justify-around mt-5 lg:col-span-1 lg:mt-0">
              <Button variant="sm" className="bg-brand-red" onClick={handleCancelClick}>
                Cancel Subscription
              </Button>
              <Button variant="sm" className="" onClick={handleUpdatePaymentClick}>
                Update Payment Method
              </Button>
            </div>
          )}
        </div>

        {isCancelModalOpen && (
          <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
            <div className="bg-white p-6 rounded-lg w-96 shadow-lg relative">
              <button
                onClick={handleCloseModal}
                className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
              <h3 className="text-lg font-semibold text-gray-700">
                Are you sure you want to cancel your subscription?
              </h3>
              <div className="mt-4 flex justify-end space-x-4">
                <Button
                  className="bg-brand-red text-white hover:bg-red-700"
                  onClick={handleConfirmCancel}
                >
                  Yes
                </Button>
                <Button
                  className="bg-gray-300 text-gray-700 hover:bg-gray-400"
                  onClick={handleCloseModal}
                >
                  No
                </Button>
              </div>
            </div>
          </div>
        )}

        <UpdatePaymentForm
          isOpen={isUpdateModalOpen}
          onClose={handleCloseUpdateModal}
          stripeCustomerId={subscriptionDetails?.customerId}
        />
      </div>
    </>
  )
}

function SubscriptionSignup(props: PageProps) {
  const { discountPrices, prices, userId } = props
  const router = useRouter()
  const [createCheckoutMutation, { isPending: isLoading }] = useMutation(
    startMapTestingSubscription
  )
  const [subscriptionUpgradeStatus] = useQuery(getSubscriptionUpgradeStatus, null)
  const { isSubscribed, maxStudentsToDiscount = 0 } = subscriptionUpgradeStatus ?? {}
  const isEligibleForUpgrade = maxStudentsToDiscount > 0

  const stripe = useStripe()
  const stripeElements = useElements()

  const handleSubscriptionCanceled = () => {
    invalidateQuery(getSubscriptionUpgradeStatus)
  }
  const priceClass = isEligibleForUpgrade
    ? "text-5xl font-extrabold text-gray-400 line-through"
    : "text-5xl font-extrabold text-gray-900"

  return (
    <div className="bg-gray-100">
      <div className="pt-12 sm:pt-16 lg:pt-20">
        <div className="max-w-screen-xl px-4 mx-auto sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-extrabold leading-9 text-gray-900 sm:text-4xl sm:leading-10 lg:text-5xl lg:leading-none">
              MAP Testing Subscription
            </h2>
          </div>
        </div>
      </div>
      <div className="mt-8 sm:mt-12 ">
        <div className="relative">
          <div className="relative max-w-3xl px-4 mx-auto sm:px-6 lg:px-8">
            <div className="grid-cols-1 mx-auto overflow-hidden rounded-lg shadow-lg lg:max-w-none lg:grid ">
              <div className="col-span-3 px-6 py-8 bg-white lg:p-12">
                <h3 className="text-2xl font-extrabold leading-8 text-gray-900 sm:text-3xl sm:leading-9">
                  Fall, Winter, and Spring Testing
                </h3>
                <p className="mt-6 text-base leading-6 text-gray-500">
                  The most affordable way to track your children’s academic growth throughout the
                  school year. Choose the yearly plan for the most savings!
                </p>
                <div className="mt-8">
                  <div className="flex items-center">
                    <h4 className="flex-shrink-0 pr-4 text-sm font-semibold leading-5 tracking-wider uppercase bg-white text-primary-600">
                      Benefits
                    </h4>
                    <div className="flex-1 border-t-2 border-gray-200"></div>
                  </div>
                  <ul className="mt-8 lg:grid lg:grid-cols-2 lg:gap-x-8 lg:gap-y-5">
                    <li className="flex items-start lg:col-span-1">
                      <div className="flex-shrink-0">
                        {/* Heroicon name: check-circle */}
                        <svg
                          className="w-5 h-5 text-green-500"
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                      <p className="ml-3 text-sm leading-5 text-gray-700">Save up to 10%</p>
                    </li>
                    <li className="flex items-start mt-5 lg:col-span-1 lg:mt-0">
                      <div className="flex-shrink-0">
                        {/* Heroicon name: check-circle */}
                        <svg
                          className="w-5 h-5 text-green-500"
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                      <p className="ml-3 text-sm leading-5 text-gray-700">
                        Premium Access to HSB Experts
                      </p>
                    </li>
                    <li className="flex items-start mt-5 lg:col-span-1 lg:mt-0">
                      <div className="flex-shrink-0">
                        {/* Heroicon name: check-circle */}
                        <svg
                          className="w-5 h-5 text-green-500"
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                      <p className="ml-3 text-sm leading-5 text-gray-700">Seamless booking</p>
                    </li>
                    <li className="flex items-start mt-5 lg:col-span-1 lg:mt-0">
                      <div className="flex-shrink-0">
                        {/* Heroicon name: check-circle */}
                        <svg
                          className="w-5 h-5 text-green-500"
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                      <p className="ml-3 text-sm leading-5 text-gray-700">Members-only Discounts</p>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="h-full col-span-2 px-6 py-8 text-center bg-gray-50 lg:flex lg:flex-col lg:justify-center lg:p-12">
                {userId && userId > 0 ? (
                  isSubscribed ? (
                    <ManageSubscriptions onCancelSubscription={handleSubscriptionCanceled} />
                  ) : (
                    <Form
                      schema={MapTestingSubscriptionCheckoutInput.omit({ sourceId: true }).extend({
                        creditCard: z.literal(true),
                        agreeToPayBalance: z.literal(true),
                      })}
                      initialValues={{
                        numberOfStudents: 1,
                        billingType: "year",
                      }}
                      onSubmit={async (values) => {
                        try {
                          assert(stripe, "stripe in subscriptions is empty!")
                          const cardElement = stripeElements?.getElement(StripeCardElement)
                          const { source, error } = await stripe.createSource(cardElement!, {
                            type: "card",
                          })
                          if (error) throw error
                          await createCheckoutMutation({
                            ...values,
                            sourceId: source!.id,
                          })
                          router.push("/map-testing")
                        } catch (error) {
                          return {
                            [FORM_ERROR]:
                              "Sorry, we had an unexpected error. Please try again. - " +
                              error.toString(),
                          }
                        }
                      }}
                    >
                      {({ values }) => (
                        <>
                          <div className="flex flex-col items-center justify-center mb-10 text-base font-medium text-gray-500">
                            <div className="flex items-center space-x-2">
                              <span className={priceClass}>
                                {USDNoCents(
                                  computeMapTestingSubscriptionTotal(
                                    prices,
                                    values.billingType,
                                    values.numberOfStudents
                                  )
                                )}
                              </span>
                              <span className="text-lg leading-6 text-left">
                                per {values.billingType}
                              </span>
                            </div>
                            <div className="flex items-center space-x-2 ">
                              <span>for</span>
                              <LabeledTextField
                                name="numberOfStudents"
                                label="Number of Students"
                                type="number"
                                fieldProps={
                                  {
                                    parse: (v) => {
                                      const num = Number(v)
                                      return num > 0 ? num : 1
                                    },
                                  } as any
                                }
                                hiddenLabel={true}
                                outerProps={{
                                  className: "self-start ml-px",
                                  style: { maxWidth: "3.5rem" },
                                }}
                                className="font-extrabold text-black"
                              />
                              <span>{plur("student", values.numberOfStudents)}</span>
                            </div>
                            <div className="flex items-center mt-4 text-xs italic text-green-500">
                              Additional students are{" "}
                              {USDNoCents(prices[values.billingType].rest / 100)} each
                            </div>
                            {isEligibleForUpgrade && (
                              <div>
                                <hr className="border-gray-200 border-t mt-6 mb-8 h-px w-full" />
                                <div className="flex items-center mt-4 text-gray-800 leading-5 mb-4">
                                  {values.billingType === "year" ? (
                                    <div>
                                      Since you&apos;ve purchased a MAP Test within the last 30
                                      days, you&apos;re eligible to upgrade to a{" "}
                                      <span className="font-bold">yearly</span> subscription at a
                                      discounted price of your last orders. Your new total is
                                    </div>
                                  ) : (
                                    <div>
                                      Since you&apos;ve purchased a MAP Test within the last 30
                                      days, you&apos;re eligible to upgrade to a{" "}
                                      <span className="font-bold">yearly</span> subscription at a
                                      discounted price of your last orders.
                                    </div>
                                  )}
                                </div>
                                {values.billingType === "year" && (
                                  <div className="text-green-500 flex items-center justify-center space-x-2">
                                    <span className="text-5xl font-extrabold">
                                      {USDNoCents(
                                        computeMapTestingSubscriptionTotal(
                                          prices,
                                          values.billingType,
                                          values.numberOfStudents,
                                          getUpgradeSubscriptionDiscount(
                                            discountPrices.base,
                                            discountPrices.discounted,
                                            getMaxNumberOfDiscountedStudents(
                                              values.numberOfStudents,
                                              maxStudentsToDiscount
                                            )
                                          )
                                        )
                                      )}
                                    </span>
                                    <span className="text-lg leading-6 text-left">
                                      per {values.billingType}
                                    </span>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                          {/* Remove monthly payment option*/}
                          {/* <Field name="billingType">
                            {({ input }) => (
                              <div className="relative self-center bg-gray-100 rounded-lg p-0.5 flex">
                                <button
                                  onClick={() => input.onChange("month")}
                                  type="button"
                                  className={clsx(
                                    "flex-1 relative border-gray-200 rounded-md  py-2 text-sm leading-5 font-medium text-gray-700 hover:text-gray-500 focus:outline-none focus:border-primary-300 focus:ring-primary-500 focus:z-10 active:bg-gray-50 active:text-gray-800 transition ease-in-out duration-150 px-3 ",
                                    input.value === "month" && "bg-white shadow-sm"
                                  )}
                                  style={{ maxWidth: "50%" }}
                                >
                                  Monthly billing
                                </button>
                                <button
                                  onClick={() => input.onChange("year")}
                                  type="button"
                                  className={clsx(
                                    "flex-1 relative border border-transparent rounded-md py-2 text-sm leading-5 font-medium text-gray-700 hover:text-gray-500 focus:outline-none focus:border-primary-300 focus:ring-primary-500 focus:z-10 active:bg-gray-50 active:text-gray-800 transition ease-in-out duration-150 px-3",
                                    input.value === "year" && "bg-white shadow-sm"
                                  )}
                                  style={{ maxWidth: "50%" }}
                                >
                                  Yearly{" "}
                                  <span className="font-semibold text-green-500 whitespace-nowrap">
                                    Save{" "}
                                    {Math.round(
                                      100 * (1 - prices.year.first / (prices.month.first * 12))
                                    )}
                                    %
                                  </span>
                                </button>
                              </div>
                            )}
                          </Field> */}

                          <LabeledCreditCardField
                            name="creditCard"
                            label="Credit Card"
                            showZip={true}
                          />

                          <LabeledTextField
                            name="coupon"
                            label="Coupon"
                            outerProps={{ style: { maxWidth: "35%" } }}
                          />

                          <LabeledCheckboxField
                            name="agreeToPayBalance"
                            label="I accept responsibility to pay off any test credit deficit if I cancel my subscription"
                          />

                          <div className="mt-6">
                            <Button variant="lg" className="w-full" disabled={isLoading}>
                              Start Subscription
                            </Button>
                          </div>
                        </>
                      )}
                    </Form>
                  )
                ) : (
                  <div className="grid gap-10 lg:grid-cols-2">
                    <div className="space-y-3">
                      <div className="text-xl font-semibold">Previous Customer?</div>
                      <div>
                        <ButtonLink
                          href={{ pathname: "/login", query: { next: router.asPath } }}
                          variant="lg"
                        >
                          Log In
                        </ButtonLink>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <div className="text-xl font-semibold">New Customer?</div>
                      <div>
                        <ButtonLink href={{ pathname: "/book-testing" }} variant="lg" className="">
                          Book Testing
                        </ButtonLink>
                      </div>
                      <div className="text-base">
                        On the final checkout page select the subscription option
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

const MapTestingSubscriptionsPage: BlitzPage<PageProps> = (props) => {
  return createElement(
    Elements,
    { stripe: stripePromise },
    <div className="px-4 pt-2 pb-6 space-y-10 md:py-6 sm:px-6 lg:px-8">
      <Suspense fallback={<Loading />}>
        <div>
          <SubscriptionSignup
            discountPrices={props.discountPrices}
            prices={props.prices}
            userId={props.userId}
          />
        </div>
      </Suspense>
    </div>
  )
}

MapTestingSubscriptionsPage.getLayout = (page) => (
  <AppLayout title="Map Testing Subscriptions">{page}</AppLayout>
)

export default MapTestingSubscriptionsPage
